import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { MAPS_API_KEY } from "../../components/mapKey";
import url from "../../url";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

const LIBRARIES = ["places"]; // ✅ outside component

const getStatusIcon = (status) => {  // ✅ outside component
  switch (status?.toLowerCase()) {
    case "active":   return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png";
    case "inactive": return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png";
    case "invited":  return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png";
    case "pending":  return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png";
    case "requested":return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png";
    case "rejected": return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png";
    case "verified": return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png";
    default:         return "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png";
  }
};

const CompanyAdminPins = ({
  statusFilter, resetKey, dateFrom, dateTo, assignmentType, country, search,
  accountExecutiveFilter, verificationStatusFilter, stateFilter, cityFilter,
  subscriptionStatusFilter, trialStartDate, trialEndDate,
  subscriptionStartDate, subscriptionEndDate,
  selectedMapAdminId, onClearSelection,
}) => {
  const [admins, setAdmins] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isFullMap, setIsFullMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 30.3753, lng: 69.3451 });
  const [mapZoom, setMapZoom] = useState(5);

  const { token } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const mapRef = useRef(null); // ✅ map instance ref

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY,
    libraries: LIBRARIES, // ✅ stable reference
  });

  useEffect(() => {
    const fetchAllCompanyAdmins = async () => {
      setLoading(true);
      try {
        const statusParam = statusFilter && statusFilter !== "all" ? `&status=${statusFilter}` : "";
        const dateFromParam = dateFrom ? `&start_date=${dateFrom}` : "";
        const dateToParam = dateTo ? `&end_date=${dateTo}` : "";
        const assignmentTypeParam = assignmentType && assignmentType !== "all" ? `&assignment_type=${assignmentType}` : "";
        const countryParam = country ? `&country=${encodeURIComponent(country)}` : "";
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
        const accountExecutiveParam = accountExecutiveFilter && accountExecutiveFilter !== "all" ? `&account_executive_id=${accountExecutiveFilter}` : "";
        const verificationStatusParam = verificationStatusFilter && verificationStatusFilter !== "all" ? `&verification_status=${verificationStatusFilter}` : "";
        const stateParam = stateFilter ? `&state=${encodeURIComponent(stateFilter)}` : "";
        const cityParam = cityFilter ? `&city=${encodeURIComponent(cityFilter)}` : "";
        const subscriptionStatusParam = subscriptionStatusFilter && subscriptionStatusFilter !== "all" ? `&subscription_status=${subscriptionStatusFilter}` : "";
        const trialStartParam = trialStartDate ? `&trial_start_date_from=${trialStartDate}` : "";
        const trialEndParam = trialEndDate ? `&trial_end_date_to=${trialEndDate}` : "";
        const subscriptionStartParam = subscriptionStartDate ? `&subscription_start_date_from=${subscriptionStartDate}` : "";
        const subscriptionEndParam = subscriptionEndDate ? `&subscription_start_date_to=${subscriptionEndDate}` : "";

        const response = await fetch(
          `${url}company-admins?sort_by=registered&sort_order=ASC&no_pagination=true`
          + statusParam + dateFromParam + dateToParam + assignmentTypeParam
          + countryParam + searchParam + accountExecutiveParam + verificationStatusParam
          + stateParam + cityParam + subscriptionStatusParam + trialStartParam
          + trialEndParam + subscriptionStartParam + subscriptionEndParam,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const result = await response.json();
        const data = result.data?.company_admins
          ?.map((admin) => ({
            id: admin.id,
            name: admin.full_name,
            email: admin.email,
            status: admin.status,
            lat: parseFloat(admin.latitude || admin.company_latitude),
            lng: parseFloat(admin.longitude || admin.company_longitude),
          }))
          .filter((e) => !isNaN(e.lat) && !isNaN(e.lng)) || [];

        setAdmins(data);
      } catch (error) {
        console.error("Error fetching company admins:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchAllCompanyAdmins();
  }, [
    token, statusFilter, dateFrom, dateTo, assignmentType, country, search, resetKey,
    accountExecutiveFilter, verificationStatusFilter, stateFilter, cityFilter,
    subscriptionStatusFilter, trialStartDate, trialEndDate, subscriptionStartDate, subscriptionEndDate,
  ]);

  // ✅ Auto-center, zoom and open InfoWindow when row selected from table
  useEffect(() => {
    if (selectedMapAdminId && admins.length > 0) {
      const selected = admins.find((a) => a.id === selectedMapAdminId);
      if (selected) {
        if (mapRef.current) {
          mapRef.current.panTo({ lat: selected.lat, lng: selected.lng });
          mapRef.current.setZoom(10);
        }
        setMapCenter({ lat: selected.lat, lng: selected.lng });
        setMapZoom(10);
        // setSelectedMarker(selected);
      }
    } else {
      // ✅ Reset map when selection cleared
      setMapCenter({ lat: 30.3753, lng: 69.3451 });
      setMapZoom(5);
      setSelectedMarker(null);
    }
  }, [selectedMapAdminId, admins]);

  const displayedAdmins = selectedMapAdminId
    ? admins.filter((a) => a.id === selectedMapAdminId)
    : admins;

  const onMapLoad = (map) => {
    mapRef.current = map; // ✅ store map instance
  };

  // ✅ renderMarkers as function — not component — prevents remount
  const renderMarkers = () => (
    <>
      {displayedAdmins.map((admin) => (
        <Marker
          key={admin.id}
          position={{ lat: admin.lat, lng: admin.lng }}
          icon={{
            url: getStatusIcon(admin.status),
            scaledSize: new window.google.maps.Size(25, 41),
            anchor: new window.google.maps.Point(12, 41),
          }}
          onClick={() => setSelectedMarker(admin)} // ✅ onClick not onMouseOver
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
          onCloseClick={() => setSelectedMarker(null)} // ✅ onCloseClick
        >
          <div style={{ backgroundColor: "white", borderRadius: "8px", padding: "6px 10px", minWidth: "150px" }}>
            <div style={{ fontWeight: "bold" }}>{selectedMarker.name}</div>
            <div>{selectedMarker.email}</div>
          </div>
        </InfoWindow>
      )}
    </>
  );

  return (
    <div style={{
      height: "70vh", position: "relative", width: "100%", marginTop: "10px",
      borderRadius: "10px", backgroundColor: "white",
      display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column"
    }}>

      {loading && (
        <Box sx={{
          position: "absolute", inset: 0, zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "rgba(255,255,255,0.6)",
        }}>
          <CircularProgress size={50} />
        </Box>
      )}

      {/* ✅ Normal map — unmounts when full map opens */}
      {isLoaded && !isFullMap && (
        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%", borderRadius: "10px" }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad} // ✅ capture map instance
          options={{
            // ✅ Lock map when a pin is selected from table
            gestureHandling: selectedMapAdminId ? "none" : "auto",
            zoomControl: !selectedMapAdminId,
            scrollwheel: !selectedMapAdminId,
            disableDoubleClickZoom: !!selectedMapAdminId,
            draggable: !selectedMapAdminId,
          }}
        >
          {renderMarkers()}
        </GoogleMap>
      )}

      {/* Full Map Button — only when normal map is showing */}
      {!isFullMap && (
        <Box
          onClick={() => setIsFullMap(true)}
          sx={{
            position: "absolute", top: 10, right: 10, zIndex: 1000,
            backgroundColor: "#006EC2", color: "white", borderRadius: "6px",
            px: 1.5, py: 0.5, cursor: "pointer", fontSize: "14px", fontWeight: 500,
          }}
        >
          {t("fullMap")}
        </Box>
      )}

      {/* Clear Selection Button */}
      {selectedMapAdminId && !isFullMap && (
        <Box
          onClick={() => onClearSelection()}
          sx={{
            position: "absolute", top: 55, right: 10, zIndex: 3000,
            backgroundColor: "#fff", borderRadius: "50%", width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
            fontSize: "18px", fontWeight: 700,
          }}
        >
          ✕
        </Box>
      )}

      {/* ✅ Full Map Modal — only mounts when isFullMap is true */}
      {isFullMap && isLoaded && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 2000, backgroundColor: "#fff" }}>
          <Box
            onClick={() => setIsFullMap(false)}
            sx={{
              position: "absolute", top: 15, right: 15, zIndex: 3000,
              backgroundColor: "#fff", borderRadius: "50%", width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
              fontSize: "18px", fontWeight: 700,
            }}
          >
            ✕
          </Box>

          <GoogleMap
            mapContainerStyle={{ height: "100vh", width: "100vw" }}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={(map) => { mapRef.current = map; }}
            options={{
              gestureHandling: selectedMapAdminId ? "none" : "auto",
              zoomControl: !selectedMapAdminId,
              scrollwheel: !selectedMapAdminId,
              disableDoubleClickZoom: !!selectedMapAdminId,
              draggable: !selectedMapAdminId,
            }}
          >
            {renderMarkers()}
          </GoogleMap>
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
        {t("Only records with valid Latitude/Longitude are shown")}
      </Typography>
    </div>
  );
};

export default CompanyAdminPins;